export interface IFileHashResult {
	fileName: string;
	sizeBytes: number;
	contentType: string;
	hashes: {
		sha256: string;
	};
}

export interface IFileHashWorkerResponse {
	fileId: string;
	success: boolean;
	data?: IFileHashResult;
	error?: string;
}

class FileHasher {
	private worker: Worker | null = null;
	private pendingTasks = new Map<
		string,
		{
			resolve: (result: IFileHashResult) => void;
			reject: (error: Error) => void;
		}
	>();

	constructor() {
		this.initWorker();
	}

	private initWorker() {
		try {
			this.worker = new Worker(
				new URL('./fileHasherWorker', import.meta.url),
				{ type: 'module' },
			);

			this.worker.onmessage = (
				event: MessageEvent<IFileHashWorkerResponse>,
			) => {
				const { fileId, success, data, error } = event.data;
				const task = this.pendingTasks.get(fileId);

				if (task) {
					this.pendingTasks.delete(fileId);

					if (success && data) {
						task.resolve(data);
					} else {
						task.reject(
							new Error(error || 'Hash computation failed'),
						);
					}
				}
			};

			this.worker.onerror = (error) => {
				console.error('Worker error:', error);
				// Reject all pending tasks
				this.pendingTasks.forEach((task, fileId) => {
					task.reject(new Error('Worker error occurred'));
					this.pendingTasks.delete(fileId);
				});
			};
		} catch (error) {
			console.error('Failed to initialize worker:', error);
		}
	}

	async computeHashFromBuffer(
		buffer: ArrayBuffer,
		fileName: string,
		sizeBytes: number,
		contentType: string,
		fileId: string,
	): Promise<IFileHashResult> {
		if (!this.worker) {
			throw new Error('Worker not initialized');
		}

		return new Promise((resolve, reject) => {
			this.pendingTasks.set(fileId, { resolve, reject });
			const bufferCopy = buffer.slice(0);
			this.worker?.postMessage(
				{
					fileData: bufferCopy,
					fileName,
					sizeBytes,
					contentType,
					fileId,
				},
				[bufferCopy], // Transfer ownership for better performance
			);
		});
	}

	terminate() {
		if (this.worker) {
			this.worker.terminate();
			this.worker = null;
		}
		this.pendingTasks.clear();
	}
}

// Singleton instance
let hasherInstance: FileHasher | null = null;

export function getFileHasher(): FileHasher {
	if (!hasherInstance) {
		hasherInstance = new FileHasher();
	}
	return hasherInstance;
}

export function terminateFileHasher() {
	if (hasherInstance) {
		hasherInstance.terminate();
		hasherInstance = null;
	}
}
